/**
 * Загрузка GA4 и отправка событий — единственный модуль, который знает про
 * Google. Всё остальное приложение зовёт `track()` и не подозревает, куда
 * уходит результат.
 *
 * ЧЕТЫРЕ РЕШЕНИЯ, КОТОРЫЕ ЗДЕСЬ ЗАПИСАНЫ КОДОМ, А НЕ ГАЛОЧКОЙ В ИНТЕРФЕЙСЕ GA:
 *
 * 1. **Разрешения — до тега.** `consent default` кладётся в очередь синхронно,
 *    и только потом в документ добавляется `<script>`. Наоборот — значит,
 *    библиотека поставит `_ga` раньше, чем узнает про запрет.
 * 2. **Автоматический `page_view` выключен** (`send_page_view: false`). В SPA
 *    он всё равно сработал бы один раз за загрузку и посчитал бы весь визит
 *    одной страницей; просмотры шлёт роутер (`useAnalytics.ts`).
 * 3. **Адрес прибивается на каждом событии.** См. `sanitize.ts` — по умолчанию
 *    GA4 приложил бы сырой `document.location` с токеном из ссылки.
 * 4. **Рекламные сигналы выключены на уровне конфигурации**
 *    (`allow_google_signals`, `allow_ad_personalization_signals`). Политика
 *    конфиденциальности обещает, что рекламных функций нет; обещание, которое
 *    держится только настройкой в чужом кабинете, — это обещание, которое
 *    отменяется чужой мышкой.
 */
import { ACHP_GA_MEASUREMENT_ID, analyticsAvailable } from './config'
import { CONSENT_DEFAULTS, CONSENT_GRANTED, readConsent, type ConsentChoice } from './consent'
import { sanitizedLocation, sanitizeUrl } from './sanitize'

declare global {
  interface Window {
    /**
     * Имя НЕ префиксовано `achp`, в отличие от всего остального в этой папке, и
     * это разница по существу: переменные сборки и ключ localStorage делят
     * пространство имён с соседними проектами, а `dataLayer` живёт внутри одной
     * вкладки, где кроме нас никого нет. Зато его ждут под этим именем отладчик
     * GA, Tag Assistant и предпушевая проверка.
     */
    dataLayer?: unknown[]
  }
}

/**
 * Параметры события. Числа и короткие машинные коды — всё, что сюда кладут.
 * Тип нарочно узкий: `unknown` пропустил бы в аналитику объект пользователя
 * целиком, а такую ошибку замечают через полгода и в чужой системе.
 */
export type EventParams = Record<string, string | number | boolean>

let booted = false
/** Тег не доехал (блокировщик, оффлайн). Только для отладки — см. ниже. */
let tagLoadFailed = false
/** Предыдущий адрес В ПРИЛОЖЕНИИ — реферер для перехода внутри SPA. */
let lastLocation = ''

function gtag(..._args: unknown[]): void {
  // В очередь уходит именно `arguments`, а не массив: gtag.js различает свои
  // вызовы по типу элемента, и разложенный в массив вызов он молча игнорирует.
  // Параметры объявлены только ради типизации вызовов ниже.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer!.push(arguments)
}

/**
 * Адрес и реферер для очередного хита. Реферер — предыдущая страница
 * приложения, а на первом хите — тот, откуда человек пришёл, и оба проходят
 * через санитайзер: чужой адрес тоже бывает с токеном в запросе.
 */
function locationParams(pathname: string): { page_location: string; page_referrer: string } {
  const page_location = sanitizedLocation(pathname)
  const page_referrer = lastLocation
    || (typeof document !== 'undefined' ? sanitizeUrl(document.referrer) : '')
  return { page_location, page_referrer }
}

/**
 * Подключает GA4. Вызывается один раз при старте приложения; повторный вызов
 * ничего не делает. Если аналитика выключена (нет Measurement ID, сборка,
 * headless-бот) — не делает ничего и в первый раз.
 */
export function bootAnalytics(pathname: string): void {
  if (booted || !analyticsAvailable()) return
  booted = true

  window.dataLayer = window.dataLayer || []

  // ── до тега ──────────────────────────────────────────────────────────────
  gtag('consent', 'default', CONSENT_DEFAULTS)
  const stored = readConsent()
  if (stored === 'granted') gtag('consent', 'update', CONSENT_GRANTED)
  gtag('js', new Date())

  gtag('config', ACHP_GA_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...locationParams(pathname),
  })

  // ── и только теперь сам тег ──────────────────────────────────────────────
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ACHP_GA_MEASUREMENT_ID)}`
  // Блокировщик рекламы, режущий googletagmanager.com, — это не поломка
  // приложения, а обычный вторник. Обработчик нужен, чтобы такая загрузка не
  // всплывала необработанной ошибкой в консоли; состояние он не меняет —
  // события просто копятся в очереди, которую никто не разберёт, и это ровно
  // то, чего хотел человек с блокировщиком.
  script.onerror = () => {
    tagLoadFailed = true
  }
  document.head.appendChild(script)
}

/**
 * Просмотр страницы. Зовётся роутером, а не эффектом экрана: адрес — свойство
 * маршрута, и разложенный по экранам вызов забудут на следующем.
 */
export function trackPageView(pathname: string, title?: string): void {
  if (!booted) return
  const params = locationParams(pathname)
  gtag('event', 'page_view', {
    ...params,
    ...(title ? { page_title: title } : {}),
  })
  lastLocation = params.page_location
}

/**
 * Событие воронки.
 *
 * Имя — snake_case, `[a-z0-9_]`, до 40 знаков: это ограничение GA4, и событие с
 * неподходящим именем не отвергается, а молча не попадает в отчёты. Проверяется
 * здесь (и в тестах), потому что заметить это в интерфейсе GA можно только
 * через сутки.
 *
 * В параметрах — счётчики, длительности и машинные коды причин. Ни адреса
 * почты, ни имён, ни текста, который написал человек: см. `events.ts`.
 */
export function track(name: string, params: EventParams = {}): void {
  if (!booted) return
  if (!isValidEventName(name)) {
    console.warn(`[analytics] событие «${name}» не отправлено: имя не по правилам GA4`)
    return
  }
  gtag('event', name, {
    ...locationParams(currentPath()),
    ...params,
  })
}

/** Ответ на плашку. `granted` разрешает только `analytics_storage`. */
export function updateConsent(choice: ConsentChoice): void {
  if (!booted) return
  gtag('consent', 'update', choice === 'granted' ? CONSENT_GRANTED : CONSENT_DEFAULTS)
}

const EVENT_NAME = /^[a-z][a-z0-9_]{0,39}$/

export function isValidEventName(name: string): boolean {
  return EVENT_NAME.test(name)
}

function currentPath(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '/'
}

/** Только для тестов: вернуть модуль в состояние «ничего не загружено». */
export function __resetAnalyticsForTests(): void {
  booted = false
  tagLoadFailed = false
  lastLocation = ''
}

/** Загрузился ли сам тег. Читается только тестами и отладкой. */
export function analyticsTagFailed(): boolean {
  return tagLoadFailed
}
